import Seo from "../components/Seo";
import { Card, CardContent } from "../components/ui/card";
import { SEO_ROUTE_PROPS } from "../seo/metadata";

export default function TermsOfService() {
	return (
		<div
			ref={(el) => el?.scrollIntoView()}
			className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6 lg:px-8 lg:py-14"
		>
			<Seo {...SEO_ROUTE_PROPS["/terms"]} />
			<Card className="shadow-sm">
				<CardContent className="p-6 sm:p-8 lg:p-10">
					<div className="space-y-10 [&_a]:font-medium [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 [&_h1]:text-4xl [&_h1]:font-semibold [&_h1]:tracking-tight [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:tracking-tight [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:tracking-tight [&_li]:leading-7 [&_p]:leading-7 [&_section]:space-y-4 [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-6">
						<h1>Terms of Service</h1>
						<p className="border-b pb-6 text-sm text-muted-foreground">
							Last updated: February 26, 2026
						</p>

						<section>
							<p>
								These Terms of Service (&quot;Terms&quot;) govern your access to
								and use of the services provided by Clerical (&quot;we,&quot;
								&quot;us,&quot; or &quot;our&quot;). By using our services, you
								agree to be bound by these Terms. If you do not agree, do not
								use our services.
							</p>
						</section>

						<section>
							<h2>1. Services</h2>
							<p>
								Clerical provides AI-powered workflow automation services for
								immigration law firms, including but not limited to AI phone
								answering, client follow-up communications, document processing
								and data extraction, case monitoring, and integration with case
								management systems such as Docketwise. We reserve the right to
								modify, suspend, or discontinue any part of the services at any
								time with reasonable notice.
							</p>
						</section>

						<section>
							<h2>2. No Legal Advice</h2>
							<p>
								Clerical is a technology and automation service. We do not
								provide legal advice, legal representation, or legal opinions.
								Our services assist your firm with administrative and
								operational tasks only. Your firm remains solely responsible for
								all legal judgments, case strategy, and client counsel. Nothing
								in our services should be construed as the practice of law.
							</p>
						</section>

						<section>
							<h2>3. AI-Generated Output</h2>
							<p>
								Our services use artificial intelligence to process documents,
								handle phone calls, generate client communications, and extract
								data. While we strive for accuracy, AI-generated output may
								contain errors. Your firm is responsible for reviewing all
								AI-generated content before relying on it for legal filings,
								client communications, or case decisions. Clerical is not liable
								for errors in AI-generated output that your firm does not review
								or correct before use.
							</p>
						</section>

						<section>
							<h2>4. Eligibility</h2>
							<p>
								You must be at least 18 years old and have the legal authority
								to enter into these Terms on behalf of yourself or the law firm
								you represent.
							</p>
						</section>

						<section>
							<h2>5. Account Responsibilities</h2>
							<p>
								You are responsible for maintaining the confidentiality of your
								account credentials and for all activities that occur under your
								account. Given the sensitive nature of the data processed
								through our services, you agree to use reasonable security
								measures and notify us immediately of any unauthorized access.
							</p>
						</section>

						<section>
							<h2>6. Acceptable Use</h2>
							<p>You agree not to:</p>
							<ul>
								<li>
									Use the services for any unlawful purpose or in violation of
									applicable regulations
								</li>
								<li>
									Provide access to the services to unauthorized individuals
								</li>
								<li>
									Interfere with or disrupt the services or their underlying
									infrastructure
								</li>
								<li>
									Attempt to gain unauthorized access to any part of the
									services
								</li>
								<li>
									Reverse engineer, decompile, or disassemble any aspect of the
									services
								</li>
							</ul>
						</section>

						<section>
							<h2>7. Confidentiality</h2>
							<p>
								We understand that our services involve access to sensitive and
								potentially privileged information. We will treat all client
								data processed through our platform as confidential and will not
								disclose it to any third party except as necessary to provide
								the services, as authorized by your firm, or as required by law.
							</p>
						</section>

						<section>
							<h2>8. Client Data</h2>
							<p>
								Your firm retains ownership of all data provided through our
								services. We process your firm&rsquo;s client data solely to
								provide the services and in accordance with your firm&rsquo;s
								instructions. We will not use your firm&rsquo;s client data for
								any purpose other than delivering the services. We handle all
								data in accordance with our{" "}
								<a href="/privacy">Privacy Policy</a>.
							</p>
						</section>

						<section>
							<h2>9. Third-Party Integrations</h2>
							<p>
								Our services may integrate with third-party platforms such as
								Docketwise and other tools used by your firm. We are not
								responsible for the availability, accuracy, or security
								practices of third-party services. Your use of third-party
								integrations is subject to those providers&rsquo; own terms and
								policies.
							</p>
						</section>

						<section>
							<h2>10. Intellectual Property</h2>
							<p>
								All content, features, and functionality of our services —
								including software, models, logos, and documentation — are owned
								by Clerical or our licensors and are protected by intellectual
								property laws. You may not copy, modify, or distribute any part
								of our services without our prior written consent.
							</p>
						</section>

						<section>
							<h2>11. Payment &amp; Billing</h2>
							<p>
								Fees for our services are outlined in your service agreement.
								All fees are non-refundable unless otherwise stated. We reserve
								the right to change our pricing with reasonable notice.
							</p>
						</section>

						<section>
							<h2>12. Disclaimer of Warranties</h2>
							<p className="text-sm font-medium leading-7 tracking-[0.03em] uppercase">
								THE SERVICES ARE PROVIDED &quot;AS IS&quot; AND &quot;AS
								AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS
								OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF
								MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR
								NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICES WILL BE
								UNINTERRUPTED, ERROR-FREE, OR THAT AI-GENERATED OUTPUT WILL BE
								FREE OF INACCURACIES.
							</p>
						</section>

						<section>
							<h2>13. Limitation of Liability</h2>
							<p className="text-sm font-medium leading-7 tracking-[0.03em] uppercase">
								TO THE MAXIMUM EXTENT PERMITTED BY LAW, CLERICAL SHALL NOT BE
								LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
								PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, OR GOODWILL,
								ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF THE SERVICES,
								INCLUDING BUT NOT LIMITED TO ANY ERRORS OR OMISSIONS IN
								AI-GENERATED OUTPUT, WHETHER BASED ON WARRANTY, CONTRACT, TORT
								(INCLUDING NEGLIGENCE), OR ANY OTHER LEGAL THEORY.
							</p>
							<p className="text-sm font-medium leading-7 tracking-[0.03em] uppercase">
								CLERICAL&rsquo;S TOTAL AGGREGATE LIABILITY ARISING OUT OF OR IN
								CONNECTION WITH THESE TERMS SHALL NOT EXCEED THE AMOUNTS PAID BY
								YOU TO CLERICAL IN THE TWELVE (12) MONTHS PRECEDING THE EVENT
								GIVING RISE TO THE CLAIM, OR ONE HUNDRED DOLLARS ($100),
								WHICHEVER IS GREATER.
							</p>
						</section>

						<section>
							<h2>14. Indemnification</h2>
							<p>
								You agree to indemnify and hold Clerical harmless from any
								claims, damages, or expenses (including reasonable
								attorneys&rsquo; fees) arising from your use of the services,
								your violation of these Terms, or your firm&rsquo;s reliance on
								AI-generated output without appropriate review.
							</p>
						</section>

						<section>
							<h2>15. Termination</h2>
							<p>
								Either party may terminate the service relationship in
								accordance with the service agreement. We may suspend access to
								the services immediately if we reasonably believe there has been
								a violation of these Terms. Upon termination, we will delete or
								return your firm&rsquo;s client data in accordance with our data
								retention practices, unless retention is required by law.
								Sections 2, 3, 7, 8, 10, 12, 13, 14, and 16 through 20 survive
								termination.
							</p>
						</section>

						<section>
							<h2>16. Governing Law</h2>
							<p>
								These Terms are governed by and construed in accordance with the
								laws of the State of Delaware, without regard to its conflict of
								law principles.
							</p>
						</section>

						<section>
							<h2>17. Changes to These Terms</h2>
							<p>
								We may update these Terms from time to time. We will notify you
								of material changes by posting the updated Terms on our website
								and, where applicable, providing direct notice. Continued use of
								the services after changes constitutes acceptance of the revised
								Terms.
							</p>
						</section>

						<section>
							<h2>18. General Provisions</h2>
							<p>
								<strong>Entire Agreement.</strong> These Terms, together with
								any applicable service agreement and our Privacy Policy,
								constitute the complete agreement between you and Clerical
								regarding the services and supersede all prior agreements and
								understandings. In the event of a conflict between these Terms
								and a service agreement, the service agreement shall prevail.
							</p>
							<p>
								<strong>Severability.</strong> If any provision of these Terms
								is held invalid or unenforceable, that provision shall be
								enforced to the maximum extent permitted by law, and the
								remaining provisions shall remain in full force and effect.
							</p>
							<p>
								<strong>Waiver.</strong> Our failure to enforce any right or
								provision of these Terms shall not constitute a waiver of that
								right or provision. Any waiver is effective only if in writing
								and signed by a duly authorized representative of Clerical.
							</p>
							<p>
								<strong>Assignment.</strong> You may not assign or transfer
								these Terms without our prior written consent. Clerical may
								freely assign these Terms in connection with a merger,
								acquisition, or sale of assets. Subject to the foregoing, these
								Terms bind and inure to the benefit of the parties and their
								permitted assigns.
							</p>
							<p>
								<strong>Notices.</strong> We may provide notices under these
								Terms by posting to our website or by email to the address
								associated with your account. Notices are effective upon
								delivery.
							</p>
						</section>

						<section>
							<h2>19. Contact Us</h2>
							<p>
								If you have questions about these Terms, please contact us at{" "}
								<a href="mailto:support@clerical.com">support@clerical.com</a>{" "}
								or by mail at:
							</p>
							<p>
								Clerical
								<br />
								2261 Market Street STE 22734
								<br />
								San Francisco, CA 94114
							</p>
						</section>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
